import { apiClient } from "./apiClient";
import { ApiError, NetworkError } from "./errors";

/**
 * Raw-bytes upload transport, the PUT twin of download.ts.
 *
 * `apiClient` is structurally JSON-only — it JSON.stringify's every body and
 * sends a JSON Content-Type — so a file that has to reach the server as it is
 * goes through here instead. Everything else is the same contract: the
 * session cookie, the CSRF header a non-GET needs, the error envelope decoded
 * into `ApiError`, a request that never completed thrown as `NetworkError`.
 */
class BytesUploader {
  private fetchImpl: typeof fetch = (...a) => fetch(...a);

  /** Same injectable seam as apiClient, so tests can drive this with msw. */
  setFetch(f: typeof fetch) {
    this.fetchImpl = f;
  }

  /**
   * PUT `body` to `path` as application/octet-stream with `headers` on top
   * of the session ones, and decode the JSON the server answers with.
   *
   * The bytes go as a typed-array view made here: a Blob or a buffer that
   * came out of a File in another realm (an iframe, jsdom) is not one fetch
   * recognises, and the caller already holds the bytes to hash them.
   */
  async put<T>(
    path: string,
    body: ArrayBuffer,
    headers: Record<string, string> = {},
    signal?: AbortSignal,
  ): Promise<T> {
    let resp: Response;
    try {
      resp = await this.fetchImpl(path, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/octet-stream",
          "X-CSRF-Token": apiClient.getCSRF(),
          ...headers,
        },
        body: new Uint8Array(body),
        signal,
      });
    } catch (cause) {
      throw new NetworkError(cause);
    }
    const contentType = resp.headers.get("Content-Type") || "";
    const isJSON = contentType.startsWith("application/json");
    let payload: unknown = null;
    if (isJSON) {
      try {
        payload = await resp.json();
      } catch {
        // Body promised JSON and wasn't — keep the http_<status> code below.
      }
    }
    if (!resp.ok) {
      const envelope = payload as
        | { error?: { code?: string; details?: unknown; message?: string } }
        | null;
      throw new ApiError(
        envelope?.error?.code ?? `http_${resp.status}`,
        resp.status,
        envelope?.error?.details,
        envelope?.error?.message,
      );
    }
    return payload as T;
  }
}

export const bytesUploader = new BytesUploader();
